import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cmsFetch } from "@jambushrushti/services/adminAuth";

export interface VisitorFeedback {
  id: string;
  name: string | null;
  email: string | null;
  phone_number?: string | null;
  phone?: string | null;
  feedback_type: string;
  message: string;
  rating: number | null;
  is_read: boolean;
  created_at?: string;
  created?: string;
}

const getBaseUrl = () =>
  import.meta.env.DEV
    ? "/jambushrusti-api"
    : (
        import.meta.env.VITE_API_BASE_URL ||
        "https://api.jambushusti.universal-software.in/api"
      ).replace(/\/$/, "");

export const useVisitorFeedback = () => {
  return useQuery({
    queryKey: ["visitor-feedback"],
    queryFn: async () => {
      const response = await cmsFetch(
        `${getBaseUrl()}/jambushrusti/cms/visitor-feedback/`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch feedback");
      }
      const data = await response.json();
      return (data.results || data) as VisitorFeedback[];
    },
  });
};

export const useSubmitFeedback = () => {
  return useMutation({
    mutationFn: async (feedback: {
      name?: string;
      email?: string;
      phone_number?: string;
      phone?: string;
      feedback_type?: string;
      message: string;
      rating?: number;
    }) => {
      const tenantId =
        import.meta.env.VITE_X_TENANTID ||
        "6cf833d7-bf6f-4688-9136-5674d8053b85";

      const payload = {
        ...feedback,
        phone_number: feedback.phone_number || feedback.phone,
      };

      const response = await fetch(
        `${getBaseUrl()}/jambushrusti/cms/visitor-feedback/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-ID": tenantId,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit feedback");
      }
      return await response.json();
    },
    onSuccess: () => {
      toast.success("Thank you for your feedback!");
    },
    onError: error => {
      toast.error("Failed to submit feedback: " + error.message);
    },
  });
};

export const useMarkFeedbackRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_read }: { id: string; is_read: boolean }) => {
      const response = await cmsFetch(
        `${getBaseUrl()}/jambushrusti/cms/visitor-feedback/${id}/`,
        {
          method: "PATCH",
          body: JSON.stringify({ is_read }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to mark as read");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visitor-feedback"] });
    },
  });
};

export const useDeleteFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await cmsFetch(
        `${getBaseUrl()}/jambushrusti/cms/visitor-feedback/${id}/`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to delete feedback");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visitor-feedback"] });
      toast.success("Feedback deleted");
    },
    onError: error => {
      toast.error("Failed to delete feedback: " + error.message);
    },
  });
};
